import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { DataService } from '../../services/data.service';
import * as d3 from 'd3';
import { constants } from 'projects/marketpro-ui/src/app/constants';
import { Utils } from 'marketpro-design-lib';
import { SizeOfPrize } from '../../models/statistics.model';
import { AuthService } from '../../../auth/services/auth.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'mpro-scatter',
  templateUrl: './scatter.component.html',
  styleUrls: ['./scatter.component.scss'],
})
export class Scatter implements OnChanges {
  public legendItems = constants.legendItems;
  @Input() public countries: SizeOfPrize[] = [];
  @Input() public selectedItemsList: any = [];
  public scatterData: SizeOfPrize[] = [];
  private ngUnsubscribe: Subject<void> = new Subject();
  public isFreemium: boolean;
  constructor(
    public dataService: DataService,
    private authService: AuthService
  ) {}

  public ngOnInit() {
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => (this.isFreemium = plan.isFreemium));
  }

  public destroyChart() {
    d3.select('#scatter').selectAll('svg').remove();
  }

  ngOnChanges() {
    this.destroyChart();
    this.scatterData = this.countries.map((continent) => ({
      ...continent,
      color: constants.legendItems.find(
        (continentColor) => continentColor.text === continent.continentName
      )?.color,
    }));
    this.drawChart();
  }

  protected drawChart() {
    const scatterChartContainer: HTMLDivElement | null = document.querySelector(
      '#scatter'
    ) as HTMLDivElement;

    const margin = { top: 15, right: 90, bottom: 35, left: 2 },
      width = scatterChartContainer?.clientWidth,
      height = 456;

    // append the svg object to the body of the page
    const svg = d3
      .select(scatterChartContainer)
      .append('svg')
      .attr('width', width + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);
    // Add X axis
    const largestPopulation = this.scatterData.sort(
      (a, b) => b.population - a.population
    )[0].population;
    const x = d3
      .scaleLinear()
      .domain([0, largestPopulation + largestPopulation * 0.2])
      .range([0, width]);

    svg
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(
        d3.axisBottom(x).tickFormat((d) => {
          return Utils.formatNumber(Number(d), true);
        })
      );
    svg
      .append('text')
      .attr('class', 'x label')
      .attr('text-anchor', 'end')
      .attr('x', 62)
      .attr('y', height + 30)
      .text('Population')
      .style('font-size', '0.75rem')
      .style('color', '#454545');

    // Add Y axis first element from array contains largest spending/income
    const y = d3
      .scaleLinear()
      .domain([0, this.scatterData[0].totalAmount])
      .range([height, 0]);
    svg
      .append('g')
      .attr('transform', 'translate(450)')
      .call(
        d3.axisLeft(y).tickFormat((d: any) => {
          if (d === 0) return '';
          return `$${Utils.formatNumber(Number(d), true)}`;
        })
      )
      .style('fill', 'white');

    svg
      .append('g')
      .call(d3.axisLeft(y).ticks(10).tickSizeInner(-height))
      .call((g) => g.select('.domain').remove());
    svg
      .selectAll('g.tick')
      .select('line')
      .attr('stroke', '#E4E4E4')
      .style('fill', 'white');
    svg
      .append('rect')
      .attr('x', 440)
      .attr('y', 0)
      .attr('width', 20)
      .attr('height', '100%')
      .style('fill', 'white');
    svg
      .append('text')
      .attr('class', 'y label')
      .attr('text-anchor', 'end')
      .attr('x', 0)
      .attr('y', 0)
      .attr('dy', '10px')
      .attr('transform', 'translate(440) rotate(-90)')
      .text('Total spending is USD')
      .style('font-size', '0.75rem')
      .style('color', '#454545');

    // Add dots
    svg
      .append('g')
      .selectAll('dot')
      .data(this.scatterData)
      .join('circle')
      .attr('cx', function (d: SizeOfPrize) {
        return x(d.population);
      })
      .attr('cy', function (d: SizeOfPrize) {
        return y(d.totalAmount);
      })
      .attr('r', 4)
      .style('fill', (d: any) => d.color)
      .on('mouseover', (event, d: any) => {
        d3.select(event.currentTarget).attr('stroke', 'black');
        const tooltip = d3.select('.tooltip');
        tooltip.style('opacity', 1);
        tooltip
          .html(
            `
              <h3>${this.isFreemium ? d.continentName : d.countryName} in ${
              d.year
            }</h3>
              <div style="display: flex; justify-content: space-between">
                <div>
                  <strong>${d.population.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}</strong>
                  <p>Population</p>
                </div>
                <div style="text-align: right">
                  <strong>
                  $${d.totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  </strong>
                  <p>Total spending in USD</p>
                </div>
              </div>
           `
          )
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke', 'none');
        d3.select('.tooltip').style('opacity', 0);
      })
      .on('mousemove', function (event) {
        d3.select('.tooltip')
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      });
  }
}
